export enum LabelFormatOptions {
  "HH:MM" = "HH:MM"
}

export interface ILabelOptions {
  format?: LabelFormatOptions;
  value?: string | "@u_interval" | "@u_chartInterval" | "@u_daysToSell";
}

export interface IAxisOptions {
  dataKey?: string;
  label?: ILabelOptions
  angle?: number;
  offset?: number;
  interval?: number;
  tick?: {
    label?: ILabelOptions
  }
}

export interface ITooltipOptions {
  label?: ILabelOptions;
  nameKey?: string;
  heading?: {
    key?: string;
    value?: string;
    align?: "left" | "right" | "center"
  }
};

export interface IChartOptions {
  enableCSVDownload?: boolean;
  showLegend?: boolean;
  xAxis?: IAxisOptions;
  yAxis?: IAxisOptions;
  dataKey?: string;
  tooltip?: ITooltipOptions;
  enableDropdown?: boolean;
  description?: string;
}

export function isChartOptions(obj: unknown): obj is IChartOptions {
  if (typeof (obj) !== "object" || !obj) {
    console.warn(`Chart options validation failed: ${obj} is not a valid object.`)
    return false
  }

  if ("enableDropdown" in obj) {
    if (!_isBool(obj.enableDropdown)) {
      console.warn(`Chart options validation failed: enableDropdown is not a boolean value, received: ${obj.enableDropdown}`)
      return false
    }
  }

  if ("enableCSVDownload" in obj) {
    if (!_isBool(obj.enableCSVDownload)) {
      console.warn(`Chart options validation failed: enableCSVDownload is not a boolean value, received: ${obj.enableCSVDownload}`)
      return false
    }
  }

  if ("showLegend" in obj) {
    if (!_isBool(obj.showLegend)) {
      console.warn(`Chart options validation failed: showLegend is not a boolean value, received: ${obj.showLegend}`)
      return false
    }
  }

  if ("xAxis" in obj) {
    if (!isAxisOptions(obj.xAxis)) {
      return false
    }
  }

  if ("yAxis" in obj) {
    if (!isAxisOptions(obj.yAxis)) {
      return false
    }
  }

  if ("dataKey" in obj) {
    if (!_isNonEmptyString(obj.dataKey)) {
      console.warn(`Chart options validation failed: dataKey is not a populated string, received: ${obj.dataKey}`)
      return false
    }
  }

  if ("tooltip" in obj) {
    if (!isTooltipOptions(obj.tooltip)) {
      return false
    }
  }

  if ("description" in obj) {
    if (!_isNonEmptyString(obj.description)) {
      console.warn(`Chart options validation failed: description is not a populated string, received: ${obj.description}`)
      return false
    }
  }

  return true
}

function isTooltipOptions(obj: unknown): obj is ITooltipOptions {
  if (typeof obj !== "object" || !obj) {
    console.warn(`Chart options validation failed: [tooltip] is not a valid object.`)
    return false
  }

  if ("label" in obj) {
    if (!isLabelOptions(obj.label)) {
      return false
    }
  }

  if ("nameKey" in obj) {
    if (!_isNonEmptyString(obj.nameKey)) {
      console.warn(`Chart options validation failed: [tooltip.nameKey] is not a valid string.`)
      return false
    }
  }

  if ("heading" in obj) {
    if (!obj.heading || typeof obj.heading !== "object") {
      console.warn(`Chart options validation failed: [tooltip.headign] is not a valid object.`)
      return false
    }

    if ("key" in obj.heading) {
      if (!_isNonEmptyString(obj.heading.key)) {
        console.warn(`Chart options validation failed: [tooltip.heading.key] is not a valid string.`)
        return false
      }
    }

    if ("value" in obj.heading) {
      if (!_isNonEmptyString(obj.heading.value)) {
        console.warn(`Chart options validation failed: [tooltip.heading.value] is not a valid string.`)
        return false
      }
    }

    if ("align" in obj.heading) {
      if (!_isNonEmptyString(obj.heading.align)) {
        console.warn(`Chart options validation failed: [tooltip.heading.align] is not a valid string.`)
        return false
      }

      if (!["left", "right", "center"].includes(obj.heading.align)) {
        console.warn(`Chart options validation failed: [tooltip.heading.align] is not an acceptable value. Acceptable values include "left", "right" or "center". Received ${obj.heading.align}`)
        return false
      }
    }
  }

  return true
}

function isAxisOptions(obj: unknown): obj is IAxisOptions {
  if (typeof (obj) !== "object" || !obj) {
    console.warn(`Axis options validation failed: ${obj} is not a valid object.`)
    return false
  }

  if ("dataKey" in obj) {
    if (!_isNonEmptyString(obj.dataKey)) {
      console.warn(`Axis options validation failed: dataKey is not a populated string, received: ${obj.dataKey}`)
      return false
    }
  }

  if ("label" in obj) {
    if (!isLabelOptions(obj.label)) {
      return false
    }
  }

  if ("angle" in obj) {
    if (!_isNumber(obj.angle)) {
      console.warn(`Axis options validation failed: angle is not a number, received: ${obj.angle}`)
      return false
    }
  }

  if ("offset" in obj) {
    if (!_isNumber(obj.offset)) {
      console.warn(`Axis options validation failed: offset is not a number, received: ${obj.offset}`)
      return false
    }
  }

  if ("interval" in obj) {
    if (!_isNumber(obj.interval)) {
      console.warn(`Axis options validation failed: interval is not a number, received: ${obj.interval}`)
      return false
    }
  }

  if ("tick" in obj) {
    if (typeof obj.tick !== "object" || !obj.tick) {
      console.warn(`Axis options validation failed: tick is not a valid object, received: ${obj.tick}`)
      return false
    }

    if ("label" in obj.tick) {
      if (!isLabelOptions(obj.tick.label)) {
        return false
      }
    }
  }

  return true
}

function isLabelOptions(obj: unknown): obj is ILabelOptions {
  if (typeof (obj) !== "object" || !obj) {
    console.warn(`Label options validation failed: ${obj} is not a valid object.`)
    return false
  }

  if ("format" in obj) {
    if (!_isNonEmptyString(obj.format)) {
      console.warn(`Label options validation failed: ${obj.format} is not a populated string.`)
      return false
    }

    if (!(obj.format in LabelFormatOptions)) {
      console.warn(`Label options validation failed: ${obj.format} is not a valid label format option.`)
      return false
    }
  }

  if ("value" in obj) {
    if (!_isNonEmptyString(obj.value)) {
      console.warn(`Label options validation failed: ${obj.value} is not a populated string.`)
      return false
    }
  }

  return true
}


function _isNonEmptyString(value: unknown): value is string {
  if (typeof (value) !== "string") {
    return false
  }

  return Boolean(value.length)
}

function _isNumber(value: unknown) {
  return typeof (value) === "number"
}

function _isBool(value: unknown) {
  return typeof (value) === "boolean"
}
